import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, XCircle, Award, BookOpen, Clock, ChevronRight, Download, Loader2, Linkedin, Share2, Copy, Mail, ExternalLink, Check } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import SEO from '@/components/SEO';
import SectionLabel from '@/components/SectionLabel';
import COURSE_DATA from '@/data/courseData';

const CoursePage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const certificateRef = useRef(null);
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentModule, setCurrentModule] = useState(0);
  const [completedModules, setCompletedModules] = useState([]);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [showCertificate, setShowCertificate] = useState(false);
  const [certificateForm, setCertificateForm] = useState({ name: '', email: '' });
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [showHints, setShowHints] = useState({});
  const [credentialId, setCredentialId] = useState(null);
  const [issuingCertificate, setIssuingCertificate] = useState(false);
  const [certificateIssued, setCertificateIssued] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Fetch course data from API with fallback to local data
  useEffect(() => {
    const fetchCourse = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/courses/${courseId}`);
        if (response.ok) {
          const data = await response.json();
          // Transform API data to match expected format
          setCourse({
            ...data,
            id: data.course_id,
          });
        } else {
          // Fallback to local data
          setCourse(COURSE_DATA[courseId]);
        }
      } catch (error) {
        console.log('Using local course data as fallback');
        setCourse(COURSE_DATA[courseId]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCourse();
  }, [courseId, API_URL]);

  useEffect(() => {
    // Load progress from localStorage
    const savedProgress = localStorage.getItem(`sbt_course_${courseId}`);
    if (savedProgress) {
      const progress = JSON.parse(savedProgress);
      setCompletedModules(progress.completedModules || []);
      setCurrentModule(progress.currentModule || 0);
    }
    window.scrollTo(0, 0);
  }, [courseId]);

  useEffect(() => {
    // Save progress to localStorage
    if (course) {
      localStorage.setItem(`sbt_course_${courseId}`, JSON.stringify({
        completedModules,
        currentModule
      }));
    }
  }, [completedModules, currentModule, courseId, course]);

  if (loading) {
    return (
      <div className="min-h-screen bg-sbt-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-sbt-gold animate-spin mx-auto mb-4" />
          <p className="text-sbt-gray">Loading course...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-sbt-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-4xl text-sbt-white mb-4">Course Not Found</h1>
          <Link to="/youth" className="btn-primary">Back to Courses</Link>
        </div>
      </div>
    );
  }

  const markModuleComplete = () => {
    if (!completedModules.includes(currentModule)) {
      setCompletedModules([...completedModules, currentModule]);
    }
    
    if (currentModule < course.modules.length - 1) {
      setCurrentModule(currentModule + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleQuizAnswer = (questionIndex, answerIndex) => {
    setQuizAnswers({ ...quizAnswers, [questionIndex]: answerIndex });
  };

  const submitQuiz = () => {
    let score = 0;
    course.quiz.forEach((q, index) => {
      if (quizAnswers[index] === q.correct) {
        score++;
      }
    });
    setQuizScore(score);
    setQuizSubmitted(true);
    
    const passingScore = Math.ceil(course.quiz.length * 0.7);
    if (score >= passingScore) {
      setShowCertificate(true);
    }
  };

  const passingScore = Math.ceil(course.quiz.length * 0.7);
  const passed = quizScore >= passingScore;
  const allModulesComplete = completedModules.length === course.modules.length;

  const generateCertificate = async () => {
    if (!certificateForm.name.trim()) {
      alert('Please enter your name');
      return;
    }
    
    if (!certificateForm.email.trim()) {
      alert('Please enter your email');
      return;
    }
    
    setIssuingCertificate(true);
    
    try {
      // First, issue the certificate via API to get a verifiable credential ID
      if (!certificateIssued) {
        const response = await fetch(`${API_URL}/api/certificates/issue`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            recipient_name: certificateForm.name,
            recipient_email: certificateForm.email,
            course_id: courseId,
            course_title: course.title,
            score: Math.round((quizScore / course.quiz.length) * 100),
          }),
        });
        
        if (!response.ok) {
          throw new Error('Failed to issue certificate');
        }
        
        const data = await response.json();
        setCredentialId(data.credential_id);
        setCertificateIssued(true);
      }
      
      setIssuingCertificate(false);
      setGeneratingPDF(true);
      
      // Wait for state to update and re-render with credential ID
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const certificateElement = certificateRef.current;
      const canvas = await html2canvas(certificateElement, {
        scale: 2,
        backgroundColor: '#F4F0E6',
        useCORS: true
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'px',
        format: [canvas.width, canvas.height]
      });
      
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      pdf.save(`SheBuiltThis_Certificate_${course.title.replace(/\s+/g, '_')}.pdf`);
    } catch (error) {
      console.error('Error generating certificate:', error);
      alert('Error generating certificate. Please try again.');
    } finally {
      setGeneratingPDF(false);
      setIssuingCertificate(false);
    }
  };

  // Certificate sharing functions
  const PRODUCTION_DOMAIN = process.env.REACT_APP_PUBLIC_DOMAIN || (typeof window !== 'undefined' ? window.location.origin : 'https://shebuiltthis.co');
  
  const getCertificateShareUrl = () => {
    if (credentialId) {
      return `${PRODUCTION_DOMAIN}/verify/${credentialId}`;
    }
    return `${PRODUCTION_DOMAIN}/youth`;
  };

  // LinkedIn "Share Post" - shares the certificate URL
  const shareCertificateLinkedIn = () => {
    const url = encodeURIComponent(getCertificateShareUrl());
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'noopener,noreferrer');
  };

  // LinkedIn "Add to Profile" - adds certificate to LinkedIn Certifications section
  const addCertificateToLinkedIn = () => {
    const certName = encodeURIComponent(course?.title || 'Leadership Course');
    const orgName = encodeURIComponent('She Built This');
    const issueYear = new Date().getFullYear();
    const issueMonth = new Date().getMonth() + 1;
    const certUrl = encodeURIComponent(getCertificateShareUrl());
    const certId = encodeURIComponent(credentialId || '');
    
    // LinkedIn Add to Profile URL
    const linkedInUrl = `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${certName}&organizationName=${orgName}&issueYear=${issueYear}&issueMonth=${issueMonth}&certUrl=${certUrl}&certId=${certId}`;
    window.open(linkedInUrl, '_blank', 'noopener,noreferrer');
  };

  const shareCertificateTwitter = () => {
    const text = encodeURIComponent(`I just completed "${course?.title}" on She Built This Youth Programme! 🎓 Research-backed leadership education. #SheBuiltThis #WomenInLeadership`);
    const url = encodeURIComponent(getCertificateShareUrl());
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'noopener,noreferrer');
  };

  const shareCertificateWhatsApp = () => {
    const text = encodeURIComponent(`I just completed "${course?.title}" on She Built This Youth Programme! Check out my certificate: ${getCertificateShareUrl()}`);
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const shareCertificateEmail = () => {
    const subject = encodeURIComponent(`My Certificate: ${course?.title} - She Built This`);
    const body = encodeURIComponent(`I'm excited to share that I've completed "${course?.title}" on She Built This Youth Programme!\n\nThis research-backed course covered essential leadership skills including ${course?.modules?.map(m => m.title).join(', ')}.\n\nVerify my certificate: ${getCertificateShareUrl()}\n\nCredential ID: ${credentialId}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_self');
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(getCertificateShareUrl());
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = getCertificateShareUrl();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    }
  };

  const currentModuleData = course.modules[currentModule];

  // Enhanced content renderer for academic content
  const renderContent = (content) => {
    const lines = content.trim().split('\n');
    const elements = [];
    let currentList = [];
    let currentTable = [];
    let inTable = false;
    
    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} className="list-disc list-outside ml-6 mb-4 space-y-2">
            {currentList.map((item, i) => (
              <li key={i} className="font-body text-sbt-slate leading-relaxed">{item}</li>
            ))}
          </ul>
        );
        currentList = [];
      }
    };

    const flushTable = () => {
      if (currentTable.length > 0) {
        const headerRow = currentTable[0];
        const bodyRows = currentTable.slice(1);
        elements.push(
          <div key={`table-${elements.length}`} className="overflow-x-auto mb-6">
            <table className="w-full border-collapse border border-sbt-border">
              <thead>
                <tr className="bg-sbt-green text-white">
                  {headerRow.split('|').filter(cell => cell.trim()).map((cell, j) => (
                    <th key={j} className="border border-sbt-border p-3 font-mono text-xs text-left">{cell.trim()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bodyRows.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FAF8F4]'}>
                    {row.split('|').filter(cell => cell.trim()).map((cell, j) => (
                      <td key={j} className="border border-[#1A1A1A]/10 p-3 font-body text-sm text-[#1A1A1A]">{cell.trim()}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        currentTable = [];
        inTable = false;
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Table detection
      if (line.includes('|') && line.trim().startsWith('|')) {
        flushList();
        if (line.includes('---')) continue; // Skip separator row
        currentTable.push(line);
        inTable = true;
        continue;
      } else if (inTable) {
        flushTable();
      }
      
      // Headers
      if (line.startsWith('## ')) {
        flushList();
        elements.push(
          <h2 key={i} className="font-display text-2xl lg:text-3xl font-bold text-sbt-black mt-10 mb-4">
            {line.replace('## ', '')}
          </h2>
        );
      } else if (line.startsWith('### ')) {
        flushList();
        elements.push(
          <h3 key={i} className="font-subheading text-xl font-bold text-sbt-slate mt-8 mb-3">
            {line.replace('### ', '')}
          </h3>
        );
      } else if (line.startsWith('---')) {
        flushList();
        elements.push(
          <div key={i} className="flex items-center justify-center gap-4 my-10">
            <div className="w-8 h-[1px] bg-sbt-gold" />
            <span className="text-sbt-gold">◆</span>
            <div className="w-8 h-[1px] bg-sbt-gold" />
          </div>
        );
      } else if (line.startsWith('**') && line.endsWith('**') && !line.includes(':')) {
        flushList();
        elements.push(
          <p key={i} className="font-subheading font-bold text-sbt-black mt-6 mb-2">
            {line.replace(/\*\*/g, '')}
          </p>
        );
      } else if (line.startsWith('- ')) {
        const listContent = line.replace('- ', '').replace(/\*\*/g, '');
        // Handle links in list items
        const processedContent = listContent.split(/(\[[^\]]+\]\([^)]+\))/).map((part, idx) => {
          const linkMatch = part.match(/\[([^\]]+)\]\(([^)]+)\)/);
          if (linkMatch) {
            const [, text, url] = linkMatch;
            return (
              <Link 
                key={idx} 
                to={url} 
                className="text-sbt-gold hover:underline font-medium"
              >
                {text}
              </Link>
            );
          }
          return part;
        });
        currentList.push(processedContent);
      } else if (line.startsWith('**Source:**') || line.startsWith('**Research')) {
        flushList();
        elements.push(
          <p key={i} className="font-mono text-xs text-sbt-dim bg-sbt-off-white p-3 border-l-2 border-sbt-green mb-4">
            {line.replace(/\*\*/g, '')}
          </p>
        );
      } else if (line.startsWith('**')) {
        flushList();
        const content = line.replace(/\*\*/g, '');
        elements.push(
          <p key={i} className="font-subheading font-bold text-sbt-black mt-4 mb-1">
            {content}
          </p>
        );
      } else if (line.trim() === '') {
        flushList();
      } else if (line.trim()) {
        flushList();
        // Process inline bold and links
        const processedLine = line.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/).map((part, idx) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={idx} className="font-semibold text-sbt-black">{part.slice(2, -2)}</strong>;
          }
          // Handle markdown links [text](url)
          const linkMatch = part.match(/\[([^\]]+)\]\(([^)]+)\)/);
          if (linkMatch) {
            const [, text, url] = linkMatch;
            return (
              <Link 
                key={idx} 
                to={url} 
                className="text-sbt-gold hover:underline font-medium"
              >
                {text}
              </Link>
            );
          }
          return part;
        });
        elements.push(
          <p key={i} className="font-body text-sbt-slate leading-relaxed mb-4">
            {processedLine}
          </p>
        );
      }
    }
    
    flushList();
    flushTable();
    
    return elements;
  };

  return (
    <div data-testid="course-page" className="min-h-screen bg-[#FAF9F6]">
      <SEO 
        title={`${course.title} — She Built This Academy`}
        description={course.description}
        keywords={['women leadership course', course.title, 'She Built This Academy', 'free leadership training', 'women in business education']}
        url={`https://shebuiltthis.com/youth/course/${courseId}`}
        image={course.image}
      />
      {/* Course Header */}
      <section className="bg-sbt-black py-12">
        <div className="max-w-[1200px] mx-auto px-6">
          <Link to="/youth" className="inline-flex items-center gap-2 text-sbt-gold hover:underline font-mono text-sm mb-6">
            <ArrowLeft size={16} /> Back to Courses
          </Link>
          
          <div className="flex flex-col lg:flex-row gap-8 items-start">
            <img 
              src={course.image} 
              alt={course.title}
              className="w-full lg:w-64 h-40 object-cover"
            />
            <div>
              <span className="pill-gold mb-4 inline-block">{course.duration}</span>
              <h1 className="font-display text-4xl lg:text-5xl font-bold text-sbt-white mb-2">
                {course.title}
              </h1>
              <p className="font-body text-lg text-sbt-dim">{course.subtitle}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Progress Bar */}
      <div className="bg-sbt-white border-b border-sbt-border">
        <div className="max-w-[1200px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-sm text-sbt-slate">Your Progress</span>
            <span className="font-mono text-sm text-sbt-gold">
              {completedModules.length} / {course.modules.length} modules complete
            </span>
          </div>
          <div className="w-full h-2 bg-sbt-border">
            <div 
              className="h-full bg-sbt-gold transition-all duration-300"
              style={{ width: `${(completedModules.length / course.modules.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Module List */}
          <aside className="lg:col-span-1">
            <h3 className="font-subheading text-lg font-bold text-sbt-black mb-4">Course Modules</h3>
            <nav className="space-y-2">
              {course.modules.map((module, index) => (
                <button
                  key={module.id}
                  onClick={() => { setCurrentModule(index); setShowQuiz(false); window.scrollTo(0, 0); }}
                  className={`w-full text-left p-4 flex items-center gap-3 transition-all ${
                    currentModule === index && !showQuiz
                      ? 'bg-sbt-gold text-sbt-black'
                      : completedModules.includes(index)
                      ? 'bg-sbt-white border border-sbt-green text-sbt-slate'
                      : 'bg-sbt-white border border-sbt-border text-sbt-slate hover:border-sbt-gold'
                  }`}
                  data-testid={`module-nav-${index}`}
                >
                  {completedModules.includes(index) ? (
                    <CheckCircle size={20} className="text-sbt-green flex-shrink-0" />
                  ) : (
                    <BookOpen size={20} className="flex-shrink-0" />
                  )}
                  <span className="font-mono text-sm">{module.title}</span>
                </button>
              ))}
              
              {/* Quiz Button */}
              <button
                onClick={() => { setShowQuiz(true); window.scrollTo(0, 0); }}
                disabled={!allModulesComplete}
                className={`w-full text-left p-4 flex items-center gap-3 transition-all ${
                  showQuiz
                    ? 'bg-sbt-gold text-sbt-black'
                    : allModulesComplete
                    ? 'bg-sbt-white border border-sbt-gold text-sbt-slate hover:bg-sbt-gold hover:text-sbt-black'
                    : 'bg-sbt-border text-sbt-dim cursor-not-allowed'
                }`}
                data-testid="quiz-nav-button"
              >
                <Award size={20} className="flex-shrink-0" />
                <span className="font-mono text-sm">Final Quiz</span>
              </button>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3">
            {!showQuiz ? (
              /* Module Content */
              <div className="bg-sbt-white p-8 lg:p-12" data-testid="module-content">
                <div className="flex items-center gap-2 text-sbt-dim mb-4">
                  <span className="font-mono text-xs">Module {currentModule + 1} of {course.modules.length}</span>
                  <span>•</span>
                  <Clock size={14} />
                  <span className="font-mono text-xs">~15 min read</span>
                </div>
                
                <h2 className="font-display text-3xl lg:text-4xl font-bold text-sbt-black mb-8">
                  {currentModuleData.title}
                </h2>
                
                {/* Render academic content */}
                <div className="prose prose-lg max-w-none">
                  {renderContent(currentModuleData.content)}
                </div>

                <div className="mt-12 pt-8 border-t border-sbt-border flex justify-between items-center">
                  <button
                    onClick={() => { setCurrentModule(Math.max(0, currentModule - 1)); window.scrollTo(0, 0); }}
                    disabled={currentModule === 0}
                    className={`flex items-center gap-2 font-mono text-sm ${
                      currentModule === 0 ? 'text-sbt-dim cursor-not-allowed' : 'text-sbt-slate hover:text-sbt-gold'
                    }`}
                  >
                    <ArrowLeft size={16} /> Previous Module
                  </button>
                  
                  {currentModule === course.modules.length - 1 ? (
                    <button
                      onClick={() => { markModuleComplete(); setShowQuiz(true); }}
                      className="btn-primary"
                      data-testid="start-quiz-btn"
                    >
                      Complete & Take Quiz <ArrowRight className="ml-2 inline" size={16} />
                    </button>
                  ) : (
                    <button
                      onClick={markModuleComplete}
                      className="btn-primary"
                      data-testid="next-module-btn"
                    >
                      Mark Complete & Continue <ArrowRight className="ml-2 inline" size={16} />
                    </button>
                  )}
                </div>
              </div>
            ) : (
              /* Quiz Section */
              <div className="bg-sbt-white p-8 lg:p-12" data-testid="quiz-section">
                {!quizSubmitted ? (
                  <>
                    <SectionLabel>Final Assessment</SectionLabel>
                    <h2 className="font-display text-3xl lg:text-4xl font-bold text-sbt-black mt-4 mb-2">
                      {course.title} Quiz
                    </h2>
                    <p className="font-body text-sbt-dim mb-8">
                      Answer at least {passingScore} out of {course.quiz.length} questions correctly to pass (70%).
                    </p>
                    
                    <div className="space-y-8">
                      {course.quiz.map((q, qIndex) => (
                        <div key={qIndex} className="p-6 bg-sbt-off-white">
                          <p className="font-subheading font-bold text-sbt-black mb-4">
                            {qIndex + 1}. {q.question}
                          </p>
                          <div className="space-y-3">
                            {q.options.map((option, oIndex) => (
                              <label
                                key={oIndex}
                                className={`flex items-center gap-3 p-4 cursor-pointer transition-all ${
                                  quizAnswers[qIndex] === oIndex
                                    ? 'bg-sbt-gold text-sbt-black'
                                    : 'bg-sbt-white hover:bg-sbt-gold-light'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name={`question-${qIndex}`}
                                  checked={quizAnswers[qIndex] === oIndex}
                                  onChange={() => handleQuizAnswer(qIndex, oIndex)}
                                  className="sr-only"
                                />
                                <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                  quizAnswers[qIndex] === oIndex ? 'border-sbt-black bg-sbt-black' : 'border-sbt-dim'
                                }`}>
                                  {quizAnswers[qIndex] === oIndex && (
                                    <span className="w-2 h-2 rounded-full bg-sbt-gold" />
                                  )}
                                </span>
                                <span className="font-body">{option}</span>
                              </label>
                            ))}
                          </div>
                          
                          {/* Hint Section */}
                          {q.hint && (
                            <div className="mt-4 pt-4 border-t border-sbt-border">
                              <button
                                type="button"
                                onClick={() => setShowHints({ ...showHints, [qIndex]: !showHints[qIndex] })}
                                className="font-mono text-xs text-sbt-green hover:text-sbt-gold transition-colors flex items-center gap-2"
                              >
                                <span className="text-lg">💡</span>
                                {showHints[qIndex] ? 'Hide Hint' : 'Need a Hint?'}
                              </button>
                              {showHints[qIndex] && (
                                <p className="mt-3 p-4 bg-sbt-green/10 border-l-4 border-sbt-green font-body text-sm text-sbt-slate italic">
                                  {q.hint}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-8">
                      <button
                        onClick={submitQuiz}
                        disabled={Object.keys(quizAnswers).length !== course.quiz.length}
                        className={`btn-primary ${
                          Object.keys(quizAnswers).length !== course.quiz.length ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        data-testid="submit-quiz-btn"
                      >
                        Submit Quiz
                      </button>
                      <p className="font-mono text-xs text-sbt-dim mt-2">
                        {Object.keys(quizAnswers).length} / {course.quiz.length} questions answered
                      </p>
                    </div>
                  </>
                ) : !showCertificate ? (
                  /* Quiz Results - Failed */
                  <div className="text-center py-12">
                    <XCircle size={64} className="text-red-500 mx-auto mb-6" />
                    <h2 className="font-display text-3xl font-bold text-sbt-black mb-4">
                      Keep Trying!
                    </h2>
                    <p className="font-body text-lg text-sbt-slate mb-2">
                      You scored {quizScore} out of {course.quiz.length}
                    </p>
                    <p className="font-body text-sbt-dim mb-8">
                      You need {passingScore} correct answers to pass. Review the material and try again.
                    </p>
                    <button
                      onClick={() => {
                        setQuizSubmitted(false);
                        setQuizAnswers({});
                        setShowQuiz(false);
                        setCurrentModule(0);
                        window.scrollTo(0, 0);
                      }}
                      className="btn-primary"
                    >
                      Review Course Material
                    </button>
                  </div>
                ) : (
                  /* Certificate Form & Generation */
                  <div data-testid="certificate-section">
                    <div className="text-center mb-8">
                      <CheckCircle size={64} className="text-sbt-green mx-auto mb-6" />
                      <h2 className="font-display text-3xl font-bold text-sbt-black mb-4">
                        Congratulations!
                      </h2>
                      <p className="font-body text-lg text-sbt-slate">
                        You scored {quizScore} out of {course.quiz.length} and passed the course!
                      </p>
                    </div>
                    
                    {/* Certificate Form */}
                    <div className="max-w-md mx-auto mb-8">
                      <h3 className="font-subheading text-xl font-bold text-sbt-black mb-4 text-center">
                        Enter Your Details for Certificate
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="font-mono text-xs text-sbt-slate block mb-2">Full Name *</label>
                          <input
                            type="text"
                            value={certificateForm.name}
                            onChange={(e) => setCertificateForm({ ...certificateForm, name: e.target.value })}
                            className="input-field"
                            placeholder="Enter your full name"
                            data-testid="certificate-name-input"
                          />
                        </div>
                        <div>
                          <label className="font-mono text-xs text-sbt-slate block mb-2">Email *</label>
                          <input
                            type="email"
                            value={certificateForm.email}
                            onChange={(e) => setCertificateForm({ ...certificateForm, email: e.target.value })}
                            className="input-field"
                            placeholder="Enter your email"
                            data-testid="certificate-email-input"
                          />
                          <p className="font-mono text-[10px] text-sbt-dim mt-1">Required for certificate verification</p>
                        </div>
                        <button
                          onClick={generateCertificate}
                          disabled={generatingPDF || issuingCertificate || !certificateForm.name.trim() || !certificateForm.email.trim()}
                          className={`btn-primary w-full flex items-center justify-center gap-2 ${
                            (generatingPDF || issuingCertificate || !certificateForm.name.trim() || !certificateForm.email.trim()) ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          data-testid="download-certificate-btn"
                        >
                          {issuingCertificate ? (
                            <>Issuing Certificate...</>
                          ) : generatingPDF ? (
                            <>Generating PDF...</>
                          ) : certificateIssued ? (
                            <>
                              <Download size={18} />
                              Download Certificate Again
                            </>
                          ) : (
                            <>
                              <Download size={18} />
                              Issue & Download Certificate
                            </>
                          )}
                        </button>
                        
                        {/* Verification Link after certificate is issued */}
                        {certificateIssued && credentialId && (
                          <div className="mt-4 p-3 bg-sbt-green/10 border border-sbt-green/30 rounded text-center">
                            <p className="font-mono text-xs text-sbt-green mb-1">Certificate Issued Successfully!</p>
                            <p className="font-mono text-sm text-sbt-black">
                              Credential ID: <strong>{credentialId}</strong>
                            </p>
                            <a 
                              href={`/verify/${credentialId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-mono text-xs text-sbt-gold hover:underline mt-2 inline-block"
                            >
                              Verify at shebuiltthis.co/verify/{credentialId} →
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Certificate Preview (Hidden for PDF generation) */}
                    <div className="overflow-hidden">
                      <div 
                        ref={certificateRef}
                        className="mx-auto"
                        style={{ width: '900px', height: '636px', background: 'linear-gradient(135deg, #FAF8F2 0%, #F5F3ED 100%)' }}
                      >
                        {/* Stanford-Level Professional Certificate Design */}
                        <div className="relative h-full p-8">
                          {/* Outer decorative border */}
                          <div className="absolute inset-4 border-2 border-[#C25E3E]/30" />
                          <div className="absolute inset-6 border border-[#C25E3E]/50" />
                          
                          {/* Main content area */}
                          <div className="relative h-full bg-[#1A1A1A]/95 p-8 flex flex-col" style={{ margin: '20px' }}>
                            
                            {/* Header with Institution Branding */}
                            <div className="text-center mb-4">
                              {/* Logo/Brand Mark */}
                              <div className="flex items-center justify-center gap-3 mb-3">
                                <div className="w-12 h-[2px] bg-[#C25E3E]" />
                                <div className="w-3 h-3 border-2 border-[#C25E3E] rotate-45" />
                                <div className="w-12 h-[2px] bg-[#C25E3E]" />
                              </div>
                              <p style={{ fontFamily: 'DM Mono, monospace', fontSize: '10px', letterSpacing: '0.35em', color: '#2E8B57' }}>
                                SHE BUILT THIS ACADEMY
                              </p>
                              <p style={{ fontFamily: 'DM Mono, monospace', fontSize: '8px', letterSpacing: '0.2em', color: '#8A9A90', marginTop: '4px' }}>
                                LEADERSHIP DEVELOPMENT PROGRAMME
                              </p>
                            </div>
                            
                            {/* Certificate Title */}
                            <div className="text-center mb-2">
                              <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '36px', fontWeight: '600', color: '#1A1A1A', letterSpacing: '0.02em' }}>
                                Certificate of Completion
                              </h1>
                            </div>
                            
                            {/* Gold Ornamental Divider */}
                            <div className="flex items-center justify-center gap-4 mb-4">
                              <div className="w-20 h-[1px] bg-gradient-to-r from-transparent to-[#C25E3E]" />
                              <div className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 bg-[#C25E3E] rotate-45" />
                                <div className="w-2 h-2 border border-[#C25E3E] rotate-45" />
                                <div className="w-1.5 h-1.5 bg-[#C25E3E] rotate-45" />
                              </div>
                              <div className="w-20 h-[1px] bg-gradient-to-l from-transparent to-[#C25E3E]" />
                            </div>
                            
                            {/* Certificate Body */}
                            <div className="text-center flex-1 flex flex-col justify-center">
                              <p style={{ fontFamily: 'Lora, serif', fontSize: '13px', color: '#5A6A60', marginBottom: '8px' }}>
                                This certifies that
                              </p>
                              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '32px', fontWeight: '700', color: '#1A1A1A', marginBottom: '8px', borderBottom: '2px solid #C25E3E', display: 'inline-block', padding: '0 40px 4px', margin: '0 auto' }}>
                                {certificateForm.name || 'Your Name'}
                              </p>
                              <p style={{ fontFamily: 'Lora, serif', fontSize: '13px', color: '#5A6A60', marginTop: '12px', marginBottom: '8px' }}>
                                has successfully completed the course
                              </p>
                              <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '24px', fontWeight: '700', color: '#2E8B57', marginBottom: '4px' }}>
                                {course.title}
                              </p>
                              <p style={{ fontFamily: 'Lora, serif', fontSize: '12px', fontStyle: 'italic', color: '#8A9A90', marginBottom: '12px' }}>
                                {course.subtitle}
                              </p>
                              
                              {/* Course Details */}
                              <div className="flex items-center justify-center gap-8 mt-2">
                                <div className="text-center">
                                  <p style={{ fontFamily: 'DM Mono, monospace', fontSize: '9px', color: '#8A9A90', letterSpacing: '0.1em' }}>DURATION</p>
                                  <p style={{ fontFamily: 'Lora, serif', fontSize: '12px', color: '#1A1A1A' }}>{course.duration}</p>
                                </div>
                                <div className="w-[1px] h-6 bg-[#C25E3E]/50" />
                                <div className="text-center">
                                  <p style={{ fontFamily: 'DM Mono, monospace', fontSize: '9px', color: '#8A9A90', letterSpacing: '0.1em' }}>ASSESSMENT</p>
                                  <p style={{ fontFamily: 'Lora, serif', fontSize: '12px', color: '#1A1A1A' }}>{quizScore}/{course.quiz.length} ({Math.round((quizScore/course.quiz.length)*100)}%)</p>
                                </div>
                                <div className="w-[1px] h-6 bg-[#C25E3E]/50" />
                                <div className="text-center">
                                  <p style={{ fontFamily: 'DM Mono, monospace', fontSize: '9px', color: '#8A9A90', letterSpacing: '0.1em' }}>MODULES</p>
                                  <p style={{ fontFamily: 'Lora, serif', fontSize: '12px', color: '#1A1A1A' }}>{course.modules.length} Completed</p>
                                </div>
                              </div>
                            </div>
                            
                            {/* Signature Section */}
                            <div className="mt-4 pt-4 border-t border-[#E0DDD5]">
                              <div className="flex justify-between items-end">
                                {/* Date */}
                                <div className="text-left w-1/4">
                                  <p style={{ fontFamily: 'DM Mono, monospace', fontSize: '8px', color: '#8A9A90', letterSpacing: '0.1em', marginBottom: '4px' }}>ISSUE DATE</p>
                                  <p style={{ fontFamily: 'Lora, serif', fontSize: '11px', color: '#1A1A1A' }}>
                                    {new Date().toLocaleDateString('en-US', { 
                                      year: 'numeric', 
                                      month: 'long', 
                                      day: 'numeric' 
                                    })}
                                  </p>
                                </div>
                                
                                {/* Signature */}
                                <div className="text-center w-2/4">
                                  <div className="border-b border-[#1A1A1A] w-48 mx-auto mb-2">
                                    <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '22px', fontStyle: 'italic', color: '#1A1A1A' }}>
                                      Dr. Neha Dewan
                                    </p>
                                  </div>
                                  <p style={{ fontFamily: 'DM Mono, monospace', fontSize: '8px', color: '#2E8B57', letterSpacing: '0.15em' }}>
                                    FOUNDER & COURSES DIRECTOR
                                  </p>
                                  <p style={{ fontFamily: 'DM Mono, monospace', fontSize: '7px', color: '#8A9A90', letterSpacing: '0.1em', marginTop: '2px' }}>
                                    SHE BUILT THIS
                                  </p>
                                </div>
                                
                                {/* Certificate ID */}
                                <div className="text-right w-1/4">
                                  <p style={{ fontFamily: 'DM Mono, monospace', fontSize: '8px', color: '#8A9A90', letterSpacing: '0.1em', marginBottom: '4px' }}>CREDENTIAL ID</p>
                                  <p style={{ fontFamily: 'DM Mono, monospace', fontSize: '10px', color: '#1A1A1A' }}>
                                    {credentialId || `SBT-${Date.now().toString(36).toUpperCase()}`}
                                  </p>
                                  <p style={{ fontFamily: 'DM Mono, monospace', fontSize: '7px', color: '#8A9A90', marginTop: '2px' }}>
                                    Verify at shebuiltthis.co/verify
                                  </p>
                                </div>
                              </div>
                            </div>
                            
                            {/* Footer Branding */}
                            <div className="text-center mt-3 pt-3 border-t border-[#E0DDD5]/50">
                              <p style={{ fontFamily: 'DM Mono, monospace', fontSize: '7px', color: '#8A9A90', letterSpacing: '0.2em' }}>
                                595 VERIFIED WOMEN BUILDERS  ·  RESEARCH-BACKED CURRICULUM  ·  SHEBUILTTHIS.CO
                              </p>
                            </div>
                          </div>
                          
                          {/* Corner Ornaments */}
                          <div className="absolute top-8 left-8 w-6 h-6 border-t-2 border-l-2 border-[#C25E3E]" />
                          <div className="absolute top-8 right-8 w-6 h-6 border-t-2 border-r-2 border-[#C25E3E]" />
                          <div className="absolute bottom-8 left-8 w-6 h-6 border-b-2 border-l-2 border-[#C25E3E]" />
                          <div className="absolute bottom-8 right-8 w-6 h-6 border-b-2 border-r-2 border-[#C25E3E]" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Share Certificate Section - Premium UI */}
                    {certificateIssued && credentialId && (
                      <div className="mt-8 p-6 bg-gradient-to-br from-sbt-cream to-white border border-sbt-gold/30 rounded-lg">
                        <div className="text-center mb-6">
                          <Award className="w-10 h-10 text-sbt-gold mx-auto mb-3" />
                          <h3 className="font-display text-xl text-sbt-black mb-2">
                            Congratulations on Your Achievement!
                          </h3>
                          <p className="font-mono text-xs text-sbt-dim tracking-wider uppercase">
                            Share your certificate with the world
                          </p>
                        </div>
                        
                        {/* Primary Action: Add to LinkedIn Profile */}
                        <div className="mb-6">
                          <button
                            onClick={addCertificateToLinkedIn}
                            className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#0077B5] text-white font-mono text-sm hover:bg-[#006097] transition-all rounded-lg shadow-lg hover:shadow-xl"
                            data-testid="add-to-linkedin-profile"
                          >
                            <Linkedin size={20} />
                            <span>Add to LinkedIn Profile</span>
                            <ExternalLink size={14} className="opacity-70" />
                          </button>
                          <p className="font-mono text-[10px] text-sbt-dim text-center mt-2">
                            This will add the certificate to your LinkedIn Certifications section
                          </p>
                        </div>
                        
                        {/* Divider */}
                        <div className="flex items-center gap-4 mb-6">
                          <div className="flex-1 h-[1px] bg-sbt-gold/30" />
                          <span className="font-mono text-xs text-sbt-dim">or share directly</span>
                          <div className="flex-1 h-[1px] bg-sbt-gold/30" />
                        </div>
                        
                        {/* Secondary Sharing Options */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                          <button
                            onClick={shareCertificateLinkedIn}
                            className="flex flex-col items-center gap-2 p-3 bg-[#1A1A1A] border border-sbt-gold/20 hover:border-sbt-gold/50 rounded-lg transition-all"
                            data-testid="share-cert-linkedin"
                          >
                            <Linkedin size={20} className="text-[#0077B5]" />
                            <span className="font-mono text-[10px] text-sbt-slate">LinkedIn</span>
                          </button>
                          <button
                            onClick={shareCertificateTwitter}
                            className="flex flex-col items-center gap-2 p-3 bg-[#1A1A1A] border border-sbt-gold/20 hover:border-sbt-gold/50 rounded-lg transition-all"
                            data-testid="share-cert-twitter"
                          >
                            <Share2 size={20} className="text-black" />
                            <span className="font-mono text-[10px] text-sbt-slate">X / Twitter</span>
                          </button>
                          <button
                            onClick={shareCertificateWhatsApp}
                            className="flex flex-col items-center gap-2 p-3 bg-[#1A1A1A] border border-sbt-gold/20 hover:border-sbt-gold/50 rounded-lg transition-all"
                            data-testid="share-cert-whatsapp"
                          >
                            <Share2 size={20} className="text-[#25D366]" />
                            <span className="font-mono text-[10px] text-sbt-slate">WhatsApp</span>
                          </button>
                          <button
                            onClick={shareCertificateEmail}
                            className="flex flex-col items-center gap-2 p-3 bg-[#1A1A1A] border border-sbt-gold/20 hover:border-sbt-gold/50 rounded-lg transition-all"
                            data-testid="share-cert-email"
                          >
                            <Mail size={20} className="text-sbt-slate" />
                            <span className="font-mono text-[10px] text-sbt-slate">Email</span>
                          </button>
                        </div>
                        
                        {/* Copy Link Section */}
                        <div className="bg-sbt-cream/50 p-4 rounded-lg">
                          <p className="font-mono text-xs text-sbt-dim mb-2">Certificate verification link:</p>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              readOnly
                              value={getCertificateShareUrl()}
                              className="flex-1 font-mono text-xs bg-[#1A1A1A] border border-sbt-gold/30 rounded px-3 py-2 text-sbt-slate"
                            />
                            <button
                              onClick={copyShareLink}
                              className={`flex items-center gap-2 px-4 py-2 font-mono text-xs rounded transition-all ${
                                linkCopied 
                                  ? 'bg-sbt-green text-white' 
                                  : 'bg-sbt-gold text-sbt-black hover:bg-sbt-gold/80'
                              }`}
                              data-testid="copy-cert-link"
                            >
                              {linkCopied ? (
                                <>
                                  <Check size={14} />
                                  Copied!
                                </>
                              ) : (
                                <>
                                  <Copy size={14} />
                                  Copy
                                </>
                              )}
                            </button>
                          </div>
                          <p className="font-mono text-[10px] text-sbt-dim mt-2">
                            Credential ID: <strong>{credentialId}</strong>
                          </p>
                        </div>
                        
                        {/* Download Again Button */}
                        <div className="mt-4 text-center">
                          <button
                            onClick={generateCertificate}
                            disabled={generatingPDF}
                            className="inline-flex items-center gap-2 font-mono text-xs text-sbt-gold hover:text-sbt-gold/80 transition-all"
                          >
                            <Download size={14} />
                            {generatingPDF ? 'Generating...' : 'Download PDF Again'}
                          </button>
                        </div>
                      </div>
                    )}
                    
                    <div className="text-center mt-8">
                      <Link to="/youth" className="link-gold font-mono text-sm">
                        ← Back to All Courses
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default CoursePage;
